import type { ShelfLabel, ShelfLabelList } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, Printer, Tag } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useShelfLabels } from "../../../features/market/purchasing-queries.js";
import {
  labelPriceText,
  labelUnitPriceText,
  netContentText,
} from "../../../features/market/pricing-values.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({ ...marketSearchShape, from: localDateText, to: localDateText });

export const Route = createFileRoute("/_app/market/etiketler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: LabelsRoute,
});

/**
 * Raf etiketi basım listesi (Prompt 16 — web, aşama B; mevzuat sorusu #61): seçilen dönemde fiyatı yürürlüğe giren
 * market ürünleri. Tablo + YAZDIRMA GÖRÜNÜMÜ (raf etiketi ızgarası: ad, marka, büyük fiyat, birim fiyat ₺/kg–L,
 * barkod metni, yürürlük). Baskıda (`@media print`) YALNIZCA etiketler basılır. Net içeriği tanımsız üründe birim fiyat
 * hesaplanamaz: ekranda uyarı, baskıda "—". Okuma `market.read`.
 */
function LabelsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketLabels")}
      description={t("market.labels.description")}
      breadcrumb={[{ label: t("menu.marketLabels") }]}
    >
      {({ companyId }) => <LabelsBody companyId={companyId} />}
    </MarketPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function LabelsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId, { required: true });
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const labels = useShelfLabels(station.stationId, range.from, range.to);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;
  const stationName = station.stations.find((item) => item.id === station.stationId)?.name ?? "";

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4 print:hidden">
        <MarketStationField selection={station} allowAll={false} />
        <DateRangePicker
          label={t("market.labels.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      {labels.error ? (
        <Alert tone="danger">{errorMessage(labels.error)}</Alert>
      ) : !labels.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <LabelList list={labels.data} stationName={stationName} />
      )}
    </>
  );
}

function LabelList({ list, stationName }: { list: ShelfLabelList; stationName: string }): ReactElement {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(false);

  const columns: DataTableColumn<ShelfLabel>[] = [
    {
      id: "product",
      header: t("market.fields.product"),
      hideable: false,
      exportValue: (row) => `${row.productCode} ${row.productName}`,
      cell: (row) => (
        <span className="grid gap-0.5">
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: list.stationId }}
            className="underline underline-offset-4"
          >
            {row.productName}
          </Link>
          <span className="font-mono text-xs text-muted-foreground">
            {row.productCode}
            {row.brand ? ` · ${row.brand}` : ""}
          </span>
        </span>
      ),
    },
    {
      id: "barcode",
      header: t("market.labels.fields.barcode"),
      exportValue: (row) => row.barcode,
      cell: (row) => <span className="font-mono text-xs">{row.barcode ?? "—"}</span>,
    },
    {
      id: "shelf",
      header: t("market.fields.shelfLocation"),
      exportValue: (row) => row.shelfLocation,
      cell: (row) => row.shelfLocation ?? "—",
    },
    {
      id: "oldPrice",
      header: t("market.labels.fields.oldPrice"),
      align: "right",
      exportValue: (row) => row.oldPrice,
      cell: (row) => <span className="tabular whitespace-nowrap">{formatUnitPrice(row.oldPrice)}</span>,
    },
    {
      id: "newPrice",
      header: t("market.labels.fields.newPrice"),
      align: "right",
      exportValue: (row) => row.newPrice,
      cell: (row) => (
        <span className="tabular font-semibold whitespace-nowrap">{formatUnitPrice(row.newPrice)}</span>
      ),
    },
    {
      id: "unitPrice",
      header: t("market.labels.fields.unitPrice"),
      align: "right",
      exportValue: (row) => labelUnitPriceText(row),
      cell: (row) =>
        row.missingNetContent ? (
          <StatusBadge tone="warning">{t("market.labels.missingNetContent")}</StatusBadge>
        ) : (
          <span className="grid justify-items-end gap-0.5">
            <span className="tabular whitespace-nowrap">{labelUnitPriceText(row)}</span>
            <span className="text-xs text-muted-foreground">{netContentText(row)}</span>
          </span>
        ),
    },
    {
      id: "effectiveAt",
      header: t("market.labels.fields.effectiveAt"),
      exportValue: (row) => row.effectiveAt,
      cell: (row) => <span className="tabular">{formatDateTime(row.effectiveAt)}</span>,
    },
  ];

  return (
    <>
      {list.missingNetContentCount > 0 ? (
        <Alert
          tone="warning"
          title={t("market.labels.missingTitle", { count: list.missingNetContentCount })}
          className="print:hidden"
        >
          {t("market.labels.missingBody")}
        </Alert>
      ) : null}

      <div className="print:hidden">
        <DataTable
          caption={t("market.labels.caption")}
          columns={columns}
          rows={list.items}
          rowKey={(row) => row.productId}
          toolbar={
            <>
              <Button
                variant="outline"
                size="sm"
                aria-pressed={preview}
                disabled={list.items.length === 0}
                onClick={() => setPreview((current) => !current)}
              >
                {preview ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                {t("market.labels.preview")}
              </Button>
              <Button size="sm" disabled={list.items.length === 0} onClick={() => window.print()}>
                <Printer aria-hidden="true" />
                {t("market.labels.print")}
              </Button>
            </>
          }
          emptyState={
            <EmptyState
              headingLevel={2}
              icon={<Tag />}
              title={t("market.labels.emptyTitle")}
              description={t("market.labels.emptyDescription")}
            />
          }
          onExport={async (visible) => {
            await exportRowsToExcel({
              fileName: `etiket-listesi-${list.from}-${list.to}`,
              sheetName: t("market.labels.caption"),
              columns: visible,
              rows: list.items,
            });
          }}
        />
      </div>

      {/* Yazdırma görünümü: ekranda düğmeyle açılır; BASKIDA her zaman ve TEK BAŞINA basılır. */}
      <section
        data-print-labels
        aria-label={t("market.labels.sheet")}
        className={preview ? "grid gap-3" : "hidden print:grid"}
      >
        <p className="text-sm text-muted-foreground print:hidden">{t("market.labels.previewHint")}</p>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 print:grid-cols-3 print:gap-2">
          {list.items.map((label) => (
            <ShelfLabelCard key={label.productId} label={label} stationName={stationName} />
          ))}
        </ul>
      </section>
    </>
  );
}

function ShelfLabelCard({ label, stationName }: { label: ShelfLabel; stationName: string }): ReactElement {
  const { t } = useTranslation();
  const content = netContentText(label);
  return (
    <li
      className="grid break-inside-avoid gap-1 rounded-md border-2 border-foreground/70 bg-card p-3 text-foreground print:border-black print:bg-white print:text-black"
      data-testid="shelf-label"
    >
      <span className="text-base leading-tight font-semibold">{label.productName}</span>
      <span className="text-xs">{[label.brand, content].filter(Boolean).join(" · ") || " "}</span>
      <span className="text-3xl font-bold tabular">{labelPriceText(label.newPrice)}</span>
      <span className="text-sm tabular">
        {t("market.labels.unitPriceLine", { value: labelUnitPriceText(label) })}
      </span>
      <span className="flex flex-wrap justify-between gap-2 text-xs">
        <span className="font-mono">{label.barcode ?? "—"}</span>
        <span>{t("market.labels.effective", { date: formatDate(label.effectiveAt) })}</span>
      </span>
      <span className="text-[10px] text-muted-foreground print:text-black">
        {[stationName, label.shelfLocation].filter(Boolean).join(" · ")}
      </span>
    </li>
  );
}
